import {
  Badge,
  Button,
  buttonClass,
  Checkbox,
  Dialog,
  EmptyState,
  Notice,
  Panel,
  SegmentedControl,
} from '@mogzo/ui';
import { Inbox, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { ruleLine } from '../components/CopyToLive';
import { useProject } from '../components/ProjectLayout';
import { SelectionBar } from '../components/SelectionBar';
import { ErrorNotice, Loading } from '../components/ui';
import { ApiError, apiSend } from '../lib/api';
import { at, type TrashGroup } from '../lib/schedule';
import { useApi } from '../lib/use-api';

type Filter = 'all' | 'test' | 'prod';

const DAY_MS = 24 * 60 * 60 * 1000;
const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

const lowerFirst = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

/** A group's name, what it is, and what went with it. */
function describe(group: TrashGroup) {
  const [event] = group.events;
  const line = (rule: TrashGroup['rules'][number]) =>
    ruleLine(rule, { boards: [], events: group.events });
  if (!event) {
    const [rule] = group.rules;
    return { name: rule ? line(rule) : 'A rule', kind: 'Rule', id: '', with: '' };
  }
  const parts: string[] = [];
  const [only] = group.rules;
  if (group.rules.length === 1 && only) parts.push(`its rule (${lowerFirst(line(only))})`);
  if (group.rules.length > 1) parts.push(`its ${group.rules.length} rules`);
  if (group.scores) {
    parts.push(
      `its leaderboard’s ${group.scores.toLocaleString('en')} ${group.scores === 1 ? 'score' : 'scores'}`,
    );
  }
  return {
    name: event.name,
    kind: 'Event',
    id: event.id,
    with: parts.length ? `With ${parts.join(' and ')}` : '',
  };
}

function deletedBy(group: TrashGroup) {
  if (group.deletedFrom === 'copy') return 'In Copy to live';
  if (group.deletedFrom === 'cli') {
    return group.deletedOn ? `With the CLI, on ${group.deletedOn}` : 'With the CLI';
  }
  return 'On the dashboard';
}

function goneIn(goneAt: string, now: number) {
  const days = Math.ceil((new Date(goneAt).getTime() - now) / DAY_MS);
  if (days <= 1) return 'Within a day';
  return days <= 7 ? `In ${days} days` : day.format(new Date(goneAt));
}

/**
 * The schedule's Trash (Nov 3, 2026, the approved mockup): deleted events and rules from test
 * and live, kept 30 days. Restore puts one back where it was; Delete forever is final.
 */
export function ProjectScheduleTrash() {
  const { project } = useProject();
  const [reload, setReload] = useState(0);
  const trash = useApi<{ groups: TrashGroup[] }>(
    `/v1/dev/projects/${project.id}/schedule/trash`,
    reload,
  );
  const [filter, setFilter] = useState<Filter>('all');
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{
    tone: 'success' | 'danger' | 'warning';
    title: string;
    text?: string;
  } | null>(null);
  const [asking, setAsking] = useState<{ groups: string[]; title: string; text: string } | null>(
    null,
  );
  const refresh = () => setReload((n) => n + 1);

  const schedule = `/projects/${project.id}/schedule`;
  const back = (
    <Link to={schedule} className={buttonClass()}>
      Back to Schedule
    </Link>
  );
  if (trash.status === 'loading') return <Loading />;
  if (trash.status === 'error') return <ErrorNotice error={trash.error} />;
  const groups = trash.data.groups;
  const shown = groups.filter((g) => filter === 'all' || g.env === filter);
  const now = Date.now();
  const allPicked = shown.length > 0 && shown.every((g) => picked.includes(g.group));
  const names = new Map(groups.map((g) => [g.group, describe(g).name]));
  const envOf = new Map(groups.map((g) => [g.group, g.env === 'prod' ? 'live' : 'test']));

  async function restore(ids: string[]) {
    setBusy(true);
    try {
      const result = await apiSend<{
        restored: string[];
        failed: { group: string; message: string }[];
      }>('POST', `/v1/dev/projects/${project.id}/schedule/trash/restore`, { groups: ids });
      const restored = result.restored.length;
      const failedText = result.failed
        .map((f) => `${names.get(f.group) ?? 'One'}: ${f.message}`)
        .join(' ');
      const [first] = result.restored;
      setMessage(
        result.failed.length && restored
          ? {
              tone: 'warning',
              title: `Restored ${restored}, ${result.failed.length} wasn’t`,
              text: failedText,
            }
          : result.failed.length
            ? {
                tone: 'danger',
                title:
                  ids.length === 1
                    ? `${names.get(ids[0] ?? '') ?? 'It'} wasn’t restored`
                    : 'Nothing was restored',
                text: result.failed.map((f) => f.message).join(' '),
              }
            : {
                tone: 'success',
                title:
                  restored === 1 && first
                    ? `Restored ${names.get(first) ?? 'it'}`
                    : `Restored ${restored}`,
                text:
                  restored === 1 && first
                    ? `It’s back on ${envOf.get(first) ?? 'its schedule'}, and runs at its next time.`
                    : 'Each one is back where it was, and runs at its next time.',
              },
      );
      setPicked([]);
      refresh();
    } catch (error) {
      setMessage({ tone: 'danger', title: failure(error) });
    } finally {
      setBusy(false);
    }
  }

  async function forget(ids: string[]) {
    setBusy(true);
    try {
      await apiSend('POST', `/v1/dev/projects/${project.id}/schedule/trash/delete`, {
        groups: ids,
      });
      setMessage({ tone: 'success', title: 'Deleted for good' });
      setPicked([]);
      setAsking(null);
      refresh();
    } catch (error) {
      setMessage({ tone: 'danger', title: failure(error) });
    } finally {
      setBusy(false);
    }
  }

  const finalText = (count: number) =>
    `${count === 1 ? 'It’s' : 'They’re'} deleted for good, with any leaderboard scores. This can’t be undone.`;

  return (
    <>
      {message && (
        <Notice
          tone={message.tone}
          title={message.title}
          action={
            <Button size="sm" variant="ghost" onClick={() => setMessage(null)}>
              Close
            </Button>
          }
        >
          {message.text}
        </Notice>
      )}

      <Panel
        title="Trash"
        description="Deleted events and rules, from test and live. Restore puts one back where it was. After 30 days it’s gone for good."
        actions={
          <>
            {back}
            <Button
              variant="danger"
              disabled={!groups.length}
              onClick={() =>
                setAsking({
                  groups: groups.map((g) => g.group),
                  title: 'Empty trash?',
                  text: `Everything in Trash (${groups.length}) is deleted for good, with any leaderboard scores. This can’t be undone.`,
                })
              }
            >
              Empty trash…
            </Button>
          </>
        }
        footer={
          <>
            <span>
              {groups.length === 1 ? '1 thing in Trash' : `${groups.length} things in Trash`}
            </span>
            <span>Things in Trash don’t run and don’t count towards your events and rules.</span>
          </>
        }
        flush
      >
        <div className="border-b border-line px-5 py-3">
          <SegmentedControl
            label="Show"
            value={filter}
            onChange={(next) => {
              setFilter(next);
              setPicked([]);
            }}
            items={[
              { id: 'all', label: 'All' },
              { id: 'test', label: 'Test' },
              { id: 'prod', label: 'Live' },
            ]}
          />
        </div>
        {shown.length ? (
          <div className="mz-table-wrap">
            <table className="mz-table">
              <caption className="sr-only">Trash</caption>
              <thead>
                <tr>
                  <th scope="col" style={{ width: 44 }}>
                    <Checkbox
                      label={<span className="sr-only">Select everything in Trash</span>}
                      checked={allPicked}
                      onChange={() => setPicked(allPicked ? [] : shown.map((g) => g.group))}
                    />
                  </th>
                  <th scope="col">What</th>
                  <th scope="col">Was on</th>
                  <th scope="col">Deleted</th>
                  <th scope="col">Gone for good</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((group) => {
                  const what = describe(group);
                  const isPicked = picked.includes(group.group);
                  return (
                    <tr key={group.group} className={isPicked ? 'bg-cream' : ''}>
                      <td>
                        <Checkbox
                          label={<span className="sr-only">Select {what.name}</span>}
                          checked={isPicked}
                          onChange={() =>
                            setPicked((now) =>
                              isPicked
                                ? now.filter((g) => g !== group.group)
                                : [...now, group.group],
                            )
                          }
                        />
                      </td>
                      <td>
                        <div className="mz-cell-main">{what.name}</div>
                        <div className="mz-cell-sub">
                          {what.kind}
                          {what.id && (
                            <>
                              {' · '}
                              <span className="mz-mono">{what.id}</span>
                            </>
                          )}
                        </div>
                        {what.with && <div className="mz-cell-sub text-ink">{what.with}</div>}
                      </td>
                      <td>
                        <Badge tone={group.env === 'prod' ? 'success' : 'info'}>
                          {group.env === 'prod' ? 'Live' : 'Test'}
                        </Badge>
                      </td>
                      <td>
                        <div className="whitespace-nowrap">{at(group.deletedAt)}</div>
                        <div className="mz-cell-sub">{deletedBy(group)}</div>
                      </td>
                      <td className="whitespace-nowrap">{goneIn(group.goneAt, now)}</td>
                      <td className="text-right whitespace-nowrap">
                        <Button size="sm" onClick={() => restore([group.group])} disabled={busy}>
                          Restore
                        </Button>{' '}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy}
                          onClick={() =>
                            setAsking({
                              groups: [group.group],
                              title: `Delete ${what.name} for good?`,
                              text: finalText(1),
                            })
                          }
                        >
                          Delete forever
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon={Inbox} title="Trash is empty">
            Events and rules you delete wait here for 30 days, so you can put them back.
          </EmptyState>
        )}
      </Panel>

      {picked.length > 0 && (
        <SelectionBar>
          <strong className="mr-auto font-semibold">{picked.length} selected</strong>
          <Button size="sm" onClick={() => restore(picked)} disabled={busy}>
            Restore
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={busy}
            onClick={() =>
              setAsking({
                groups: picked,
                title: `Delete ${picked.length} ${picked.length === 1 ? 'thing' : 'things'} for good?`,
                text: finalText(picked.length),
              })
            }
          >
            Delete forever…
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={X}
            label="Clear selection"
            onClick={() => setPicked([])}
          />
        </SelectionBar>
      )}

      <Dialog
        open={asking !== null}
        title={asking?.title ?? ''}
        onClose={() => setAsking(null)}
        footer={
          <>
            <Button onClick={() => setAsking(null)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="danger" loading={busy} onClick={() => asking && forget(asking.groups)}>
              Delete for good
            </Button>
          </>
        }
      >
        <p className="m-0 leading-relaxed">{asking?.text}</p>
      </Dialog>
    </>
  );
}
